type T = any

/**
 * Batch promises manager goal is to aggregate all "batch" calls in a task, and call the doneCb with the resolved values.
 * Read about tasks here - https://developer.mozilla.org/en-US/docs/Web/API/HTML_DOM_API/Microtask_guide#tasks
 * @param doneCb - A callback that receives an array of resolved values from the given batched promise while maintaining the order of the resolved values.
 */
export const createBatchPromisesManager = (doneCb: (results: Array<T>) => void) => {
	const promisesArr = [] as Array<Promise<T>>

	const flush = () => {
		// Await for all batched promises, and call the doneCb with all the resolved values.
		Promise.all(promisesArr)
			.then(doneCb)
			.catch((e) => {
				throw new Error(`promisesArr in flush failed with error - ${e}`)
			})
		// Clear the current array from items and marks that the current batch is flushed
		promisesArr.length = 0
	}

	return {
		/**
		 * batch will execute the promise only in the next task. (next "tick")
		 * @param promise - a promise which returns a T value to handle later on the next tick bu doneCB.
		 */
		batch(promise: Promise<T>) {
			// Only when the array is empty we want to "flush" the batch.
			// The first batch would be pushed to the array and flushed, but since the flush content happens on the next task,
			// all the batches happens in the current task would be pushed into the array, and handled on the next task.
			const flushing = promisesArr.length

			promisesArr.push(promise)

			if (!flushing) {
				// Execute the flush only after the current task is done. (in the next "tick")
				Promise.resolve()
					.then(flush)
					.catch((e) => {
						throw new Error(`flush failed with error - ${e}`)
					})
			}
		},
	}
}
